import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Icon, type IconName } from '../components/icons';

/**
 * The pieces the help pages are written from.
 *
 * Documentation is authored as components rather than Markdown on purpose: a Markdown
 * renderer is a dependency and a second styling system, and this studio already draws
 * its own icons rather than take one for thirty glyphs. The cost is angle brackets around
 * the prose; the gain is that a table in the docs is the same table as everywhere else.
 */

export function Lead({ children }: { children: ReactNode }) {
  return (
    <p className="mb-6 max-w-[68ch] text-[15px] leading-relaxed text-[var(--ff-text-muted)]">
      {children}
    </p>
  );
}

export function H2({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2
      id={id}
      className="mt-10 scroll-mt-6 border-t border-[var(--ff-border)] pt-6 text-[17px]
                 font-semibold tracking-[-0.01em] first:mt-0 first:border-0 first:pt-0"
    >
      {children}
    </h2>
  );
}

export function H3({ children }: { children: ReactNode }) {
  return <h3 className="mt-6 text-[14.5px] font-semibold">{children}</h3>;
}

export function P({ children }: { children: ReactNode }) {
  return <p className="mt-3 max-w-[68ch] leading-relaxed">{children}</p>;
}

export function UL({ children }: { children: ReactNode }) {
  return (
    <ul className="mt-3 max-w-[68ch] list-disc space-y-1.5 pl-5 leading-relaxed
                   marker:text-[var(--ff-text-faint)]">
      {children}
    </ul>
  );
}

export function OL({ children }: { children: ReactNode }) {
  return (
    <ol className="mt-3 max-w-[68ch] list-decimal space-y-2 pl-5 leading-relaxed
                   marker:text-[var(--ff-text-faint)]">
      {children}
    </ol>
  );
}

/** Inline code: a path, a key, an identifier. */
export function C({ children }: { children: ReactNode }) {
  return (
    <code className="ff-mono rounded border border-[var(--ff-border)]
                     bg-[var(--ff-surface-sunken)] px-1 py-[1px] text-[12.5px]">
      {children}
    </code>
  );
}

export function Pre({ title, children }: { title?: string; children: string }) {
  return (
    <figure className="mt-4 overflow-hidden rounded-lg border border-[var(--ff-border)]">
      {title && (
        <figcaption className="border-b border-[var(--ff-border)] bg-[var(--ff-surface-raised)]
                               px-3 py-1.5 text-[12px] text-[var(--ff-text-muted)]">
          {title}
        </figcaption>
      )}
      {/* Wide commands scroll inside the block rather than widening the page. */}
      <pre className="ff-mono overflow-x-auto bg-[var(--ff-surface-sunken)] p-3 text-[12.5px]
                      leading-relaxed">
        {children}
      </pre>
    </figure>
  );
}

export function Table({
  head, rows,
}: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-[var(--ff-border)]">
      <table className="w-full border-collapse text-left text-[13.5px]">
        <thead>
          <tr className="bg-[var(--ff-surface-raised)]">
            {head.map((h) => (
              <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-[var(--ff-border)] align-top">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2 leading-relaxed">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const noteTones: Record<'note' | 'warn' | 'danger',
  { icon: IconName; ring: string; text: string }> = {
  note: { icon: 'info', ring: 'border-[var(--ff-border-strong)]', text: 'text-[var(--ff-text-muted)]' },
  warn: { icon: 'alert', ring: 'border-[var(--ff-warning)]/40', text: 'text-[var(--ff-warning)]' },
  danger: { icon: 'alert', ring: 'border-[var(--ff-danger)]/40', text: 'text-[var(--ff-danger)]' },
};

export function Note({
  tone = 'note', title, children,
}: { tone?: keyof typeof noteTones; title?: string; children: ReactNode }) {
  const t = noteTones[tone];
  return (
    <div className={clsx('mt-4 max-w-[68ch] rounded-lg border bg-[var(--ff-surface-raised)] p-3',
      t.ring)}>
      <p className={clsx('flex items-center gap-2 text-[13px] font-semibold', t.text)}>
        <Icon name={t.icon} size={15} />
        {title ?? (tone === 'note' ? 'Note' : tone === 'warn' ? 'Careful' : 'Important')}
      </p>
      <div className="mt-1.5 text-[13.5px] leading-relaxed text-[var(--ff-text)]">{children}</div>
    </div>
  );
}

/** A term and its definition — used for the vocabulary the rest of the docs assume. */
export function Terms({ items }: { items: { term: string; children: ReactNode }[] }) {
  return (
    <dl className="mt-4 max-w-[68ch] space-y-3">
      {items.map((item) => (
        <div key={item.term} className="border-l-2 border-[var(--ff-border-strong)] pl-3">
          <dt className="font-semibold">{item.term}</dt>
          <dd className="mt-0.5 leading-relaxed text-[var(--ff-text-muted)]">{item.children}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Points at a file in the repository, for a reader who wants the source of a claim. */
export function Source({ path }: { path: string }) {
  return (
    <p className="mt-2 text-[12.5px] text-[var(--ff-text-faint)]">
      Source: <span className="ff-mono">{path}</span>
    </p>
  );
}
